'use client';
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { DailySoaringForecast } from '@/lib/mockWeatherData';
import { useSettings } from '@/context/SettingsContext';

interface WeekComparisonChartProps {
  forecasts: DailySoaringForecast[];
}

function scoreColor(day: DailySoaringForecast): string {
  if (day.verdict === 'storm-risk') return 'var(--purple-cloud)';
  if (day.verdict === 'unflyable' || day.qualityScore < 15) return 'var(--danger-red)';
  if (day.qualityScore >= 50) return 'var(--success-green)';
  if (day.qualityScore >= 15) return 'var(--warning-orange)';
  return 'var(--danger-red)';
}

interface TooltipPayload {
  payload: DailySoaringForecast;
}

function CustomTooltip({
  active,
  payload,
  altUnit,
  vzUnit,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  altUnit: string;
  vzUnit: string;
}) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;

  return (
    <div
      className="rounded-xl px-4 py-3 text-xs space-y-1"
      style={{
        backgroundColor: 'var(--surface-elevated)',
        border: '1px solid var(--card-border)',
        boxShadow: 'var(--shadow-soft)',
      }}
    >
      <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{d.dayLabel}</p>
      <p style={{ color: 'var(--text-secondary)' }}>
        Score qualité: <span className="font-mono-data font-bold" style={{ color: scoreColor(d) }}>{d.qualityScore}/100</span>
      </p>
      <p style={{ color: 'var(--text-secondary)' }}>
        Base Cu: <span className="font-mono-data" style={{ color: 'var(--purple-cloud)' }}>{d.maxCumulusBase} {altUnit}</span>
      </p>
      <p style={{ color: 'var(--text-secondary)' }}>
        VZ max: <span className="font-mono-data" style={{ color: 'var(--info-blue)' }}>{d.maxVZ} {vzUnit}</span>
      </p>
      <p style={{ color: 'var(--text-secondary)' }}>
        Orage: <span className="font-mono-data" style={{ color: d.stormRisk > 45 ? 'var(--danger-red)' : 'var(--success-green)' }}>{d.stormRisk}%</span>
      </p>
    </div>
  );
}

export default function WeekComparisonChart({ forecasts }: WeekComparisonChartProps) {
  const { settings } = useSettings();
  const altUnit = settings.altitudeUnit;
  const vzUnit = settings.vzUnit;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Comparaison - Score qualité 7 jours</h3>
        <div className="flex items-center gap-3 text-xs">
          {[
            { label: 'Bonne / excellente', color: 'var(--success-green)' },
            { label: 'Journee moyenne', color: 'var(--warning-orange)' },
            { label: 'Orage', color: 'var(--purple-cloud)' },
            { label: 'Non vol', color: 'var(--danger-red)' },
          ].map((leg) => (
            <span key={`leg-${leg.label}`} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: leg.color, display: 'inline-block' }} />
              <span style={{ color: 'var(--text-secondary)' }}>{leg.label}</span>
            </span>
          ))}
        </div>
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={forecasts} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="dayLabel"
            tick={{ fill: 'var(--text-secondary)', fontSize: 11, fontFamily: 'IBM Plex Mono' }}
            axisLine={{ stroke: 'var(--border)' }}
            tickLine={false}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fill: 'var(--text-secondary)', fontSize: 10, fontFamily: 'IBM Plex Mono' }}
            axisLine={false}
            tickLine={false}
            label={{ value: 'Score /100', angle: -90, position: 'insideLeft', fill: 'var(--text-secondary)', fontSize: 9, dx: 14 }}
          />
          <Tooltip content={<CustomTooltip altUnit={altUnit} vzUnit={vzUnit} />} />
          <Bar dataKey="qualityScore" radius={[4, 4, 0, 0]} maxBarSize={40}>
            {forecasts.map((entry) => (
              <Cell key={`cell-${entry.date}`} fill={scoreColor(entry)} opacity={0.9} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
